import axios from "axios";
import FileSaver from "file-saver";
import { notification } from "antd";

import history from "../history";
import { API_BASE_URL } from "configs/AppConfig";
import { AUTH_TOKEN } from "redux/constants/Auth";
import { TENANT_KEY } from "redux/constants/Tenant";

const ENTRY_ROUTE = "/auth/welcome";
const PUBLIC_REQUEST_KEY = "public-request";
const TOKEN_PAYLOAD_KEY = "authorization";
const DEFAULT_TENANT = "tenant_base";

function resolveTenant() {
	const stored = localStorage.getItem(TENANT_KEY);
	if (stored) return stored;

	// Sessão nova (ex.: aba anônima): ainda não houve seleção de tenant.
	// Usa tenant_base como padrão enquanto o menu não popular a lista.
	const jwtToken = localStorage.getItem(AUTH_TOKEN);
	if (jwtToken) {
		localStorage.setItem(TENANT_KEY, DEFAULT_TENANT);
		return DEFAULT_TENANT;
	}

	return null;
}

function onFulfilledRequest(config) {
	const jwtToken = localStorage.getItem(AUTH_TOKEN);
	const tenant = resolveTenant();

	if (jwtToken) {
		config.headers[TOKEN_PAYLOAD_KEY] = jwtToken;
	}

	if (!jwtToken && !config.headers[PUBLIC_REQUEST_KEY]) {
		history.push(ENTRY_ROUTE);
	}

	if (tenant) {
		config.headers["x-tenant"] = tenant;
	}

	return config;
}

function handleRequestError(error) {
	notification.error({
		message: "Error",
	});
	return Promise.reject(error);
}

function handleResponseError(error) {
	if (error.config?.silentError) {
		return Promise.reject(error);
	}
	let notificationParam = {
		message: "",
	};

	if (!error.response) {
		notificationParam.message = "Erro inesperado";
		notification.error(notificationParam);
		return Promise.reject(error);
	}

	if (error.response.status === 401) {
		notificationParam.message = "Falha na autenticação";
		notificationParam.description =
			error.response.data?.message ?? "Sessão expirada ou token inválido. Faça login novamente.";
		localStorage.removeItem(AUTH_TOKEN);
		notification.error(notificationParam);
		return Promise.reject(error);
	}

	if (
		error.response.status === 404 ||
		error.response.status === 403 ||
		error.response.status === 400
	) {
		if (error.response.data?.message) {
			notificationParam.message = error.response.data.message;
		} else if (error.response.data?.error) {
			notificationParam.message = error.response.data.error;
		} else {
			notificationParam.message = "Not Found";
		}
	}

	if (error.response.status === 500) {
		if (error.response.data?.message) {
			notificationParam.message = error.response.data.message.toString();
		} else if (error.response.data?.error?.message) {
			notificationParam.message =
				error.response.data.error.message.toString();
		} else if (typeof error.response.data?.error == "string") {
			notificationParam.message = error.response.data.error.toString();
		} else {
			notificationParam.message = "Erro inesperado";
		}
	}

	if (error.response.status === 409) {
		return Promise.reject(error);
	}

	if (error.response.status === 508) {
		notificationParam.message = "Time Out";
	}

	notification.error(notificationParam);

	return Promise.reject(error);
}

const config = {
	baseURL: API_BASE_URL,
	timeout: 300000,
};

export const apiFetchClient = axios.create(config);

apiFetchClient.interceptors.request.use(onFulfilledRequest, handleRequestError);
apiFetchClient.interceptors.response.use((response) => {
	return response.data;
}, handleResponseError);

export const apiDownloadClient = axios.create({
	...config,
	responseType: "blob",
});

apiDownloadClient.interceptors.request.use(
	onFulfilledRequest,
	handleRequestError,
);
apiDownloadClient.interceptors.response.use((response) => {
	const filename = response.headers["content-disposition"]
		.split(";")[1]
		.split("=")[1];
	FileSaver.saveAs(response.data, filename);

	return response;
}, handleResponseError);

export default apiFetchClient;
